import React, { useState, useCallback } from "react";
import { v4 as uuidv4 } from "../utils/uuid";
import {
  createSession,
  getMessages,
  getSessions,
  getmodule,
  deleteModule,
  sendStreamingMessage,
} from "../api/chatService";
import { ChatContext } from "./ChatContextDefinition";

export const ChatProvider = ({ children }) => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [currentModule, setCurrentModule] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreSessions, setHasMoreSessions] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalSessions, setTotalSessions] = useState(0);

  // Modules
  const [modules, setModules] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(false);

  // Token helper
  const getToken = () => localStorage.getItem("Token");

  // Clear chat
  const clearMessages = useCallback(() => setMessages([]), []);

  // Add new message
  const addMessage = useCallback((content, sender) => {
    const newMessage = {
      id: uuidv4(),
      content,
      sender,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, newMessage]);
  }, []);

  // Fetch chat sessions
  const fetchSessions = useCallback(
    async (moduleId = null, page = 1, append = false) => {
      if (!append) {
        setSessionsLoading(true);
        setCurrentPage(1);
        setHasMoreSessions(true);
      } else {
        setIsLoadingMore(true);
      }

      try {
        const response = await getSessions(getToken(), page, moduleId);

        setSessions((prev) => {
          if (append) {
            const existingIds = new Set(prev.map((s) => s.session_id));
            const newSessions = response.sessions.filter(
              (s) => !existingIds.has(s.session_id)
            );
            return [...prev, ...newSessions];
          }
          return response.sessions || [];
        });

        setHasMoreSessions(page < response.max_pages);
        setCurrentPage(page);
        setTotalSessions(response.total_sessions || response.sessions.length);
      } catch (error) {
        console.error("Failed to load sessions:", error);
        if (!append) {
          setSessions([]);
          setHasMoreSessions(false);
        }
      } finally {
        setSessionsLoading(false);
        setIsLoadingMore(false);
      }
    },
    []
  );

  // Initialize session with module
  const initializeSession = useCallback(
    async (moduleId, moduleName) => {
      if (!moduleId) return;
      setIsLoading(true);
      try {
        const sessionId = await createSession(getToken(), moduleId);
        setCurrentSessionId(sessionId);
        setCurrentModule(moduleName);
        setCurrentPage(1);
        setHasMoreSessions(true);
        fetchSessions();

        // Name the module in the opening message itself — the one place a user
        // reads right after picking a bubble — rather than a heading that keeps
        // changing across the whole conversation. Treat the name as a label
        // (its own line) instead of the subject of a sentence, since module
        // names don't all read naturally as "You're chatting with ___".
        const activeModule = modules.find((m) => m.id === moduleId);
        const introText = activeModule
          ? `**${activeModule.name}**\n\n${activeModule.desc ? `${activeModule.desc} ` : ""}What would you like to know?`
          : "Welcome to AI Assistant! How can I help you today?";

        setMessages([
          {
            id: uuidv4(),
            content: introText,
            sender: "bot",
            timestamp: new Date(),
          },
        ]);
      } catch (error) {
        console.error("Failed to initialize session:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchSessions, modules]
  );

  // Initialize direct session (no module)
  const initializeDirectSession = useCallback(async () => {
    setIsLoading(true);
    try {
      const sessionId = await createSession(getToken(), null);
      setCurrentSessionId(sessionId);
      setCurrentModule("General Chat");
      setCurrentPage(1);
      setHasMoreSessions(true);
      fetchSessions();

      setMessages([
        {
          id: uuidv4(),
          content: "Welcome to AI Assistant! How can I help you today?",
          sender: "bot",
          timestamp: new Date(),
        },
      ]);
    } catch (error) {
      console.error("Failed to initialize direct session:", error);
      const fallbackSessionId = `direct_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      setCurrentSessionId(fallbackSessionId);
      setCurrentModule("General Chat");
      setMessages([
        {
          id: uuidv4(),
          content: "Welcome to AI Assistant! How can I help you today?",
          sender: "bot",
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [fetchSessions]);

  // Load messages for a session
  const loadSessionMessages = useCallback(async (sessionId) => {
    if (!sessionId) return;
    setIsLoading(true);
    try {
      const response = await getMessages(getToken(), sessionId);
      const loadedMessages = [];

      response.messages.forEach((msg) => {
        loadedMessages.push({
          id: uuidv4(),
          content: msg.user_message,
          sender: "user",
          timestamp: msg.timestamp,
        });
        loadedMessages.push({
          id: uuidv4(),
          content: msg.bot_response,
          sender: "bot",
          timestamp: msg.timestamp,
        });
      });

      setMessages(
        loadedMessages.length > 0
          ? [
              {
                id: uuidv4(),
                content:
                  "Welcome to AI Assistant! How can I help you today?",
                sender: "bot",
                timestamp: loadedMessages[0].timestamp,
              },
              ...loadedMessages,
            ]
          : [
              {
                id: uuidv4(),
                content:
                  "Welcome to AI Assistant! How can I help you today?",
                sender: "bot",
                timestamp: new Date(),
              },
            ]
      );
    } catch (error) {
      console.error("Failed to load messages:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Send user message and stream bot response
  const sendUserMessage = useCallback(
    async (content) => {
      if (!currentSessionId) return;

      addMessage(content, "user");
      setIsLoading(true);

      const botMessageId = uuidv4();
      let botMessageAdded = false;
      setStreamingMessageId(botMessageId);

      try {
        const isDirectSession = currentSessionId.startsWith("direct_");
        const requestBody = { user_message: content };

        if (!isDirectSession) {
          requestBody.session_id = currentSessionId;
        }

        await sendStreamingMessage(getToken(), requestBody, (partial) => {
          if (!botMessageAdded) {
            setMessages((prev) => [
              ...prev,
              {
                id: botMessageId,
                content: partial,
                sender: "bot",
                timestamp: new Date(),
              },
            ]);
            botMessageAdded = true;
          } else {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === botMessageId ? { ...m, content: partial } : m
              )
            );
          }
        });

        if (!botMessageAdded) {
          setMessages((prev) => [
            ...prev,
            {
              id: botMessageId,
              content:
                "Sorry, I couldn't process your request. Please try again.",
              sender: "bot",
              timestamp: new Date(),
            },
          ]);
        }
      } catch (error) {
        console.error(`Error: ${error.message}`);
        if (!botMessageAdded) {
          setMessages((prev) => [
            ...prev,
            {
              id: botMessageId,
              content:
                "An error occurred while processing your message. Please try again later.",
              sender: "bot",
              timestamp: new Date(),
            },
          ]);
        }
      } finally {
        setIsLoading(false);
        setStreamingMessageId(null);
      }
    },
    [currentSessionId, addMessage]
  );

  // Reset to the blank welcome screen — used by every "New Chat" entry point
  // (header +, sidebar button) so there's one consistent, session-agnostic way
  // back to the suggestion bubbles instead of a separate module picker.
  const startNewChat = useCallback(() => {
    setCurrentSessionId(null);
    setCurrentModule(null);
    setMessages([]);
  }, []);

  // Load more sessions
  const loadMoreSessions = useCallback(
    async (moduleId = null) => {
      if (hasMoreSessions && !isLoadingMore && !sessionsLoading) {
        const nextPage = currentPage + 1;
        await fetchSessions(moduleId, nextPage, true);
      }
    },
    [
      hasMoreSessions,
      isLoadingMore,
      currentPage,
      fetchSessions,
      sessionsLoading,
    ]
  );

  // Fetch all modules
  const fetchModules = useCallback(async () => {
    setModulesLoading(true);
    try {
      const response = await getmodule(getToken());
      setModules(response.modules || []);
    } catch (error) {
      console.error("Failed to load modules:", error);
    } finally {
      setModulesLoading(false);
    }
  }, []);

  // Delete module
  const handleDeleteModule = useCallback(
    async (moduleToDelete) => {
      if (!moduleToDelete) return;
      try {
        const response = await deleteModule(getToken(), moduleToDelete);
        await fetchModules();
        return response;
      } catch (error) {
        console.error("Failed to delete module:", error);
        throw error;
      }
    },
    [fetchModules]
  );

  return (
    <ChatContext.Provider
      value={{
        messages,
        isLoading,
        currentSessionId,
        currentModule,
        sendUserMessage,
        initializeSession,
        initializeDirectSession,
        loadSessionMessages,
        clearMessages,
        startNewChat,
        setCurrentSessionId,
        setCurrentModule,
        sessions,
        fetchSessions,
        sessionsLoading,
        streamingMessageId,
        loadMoreSessions,
        hasMoreSessions,
        isLoadingMore,
        currentPage,
        totalSessions,
        modules,
        modulesLoading,
        fetchModules,
        handleDeleteModule,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};
